import React, { useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useMasterData } from '../contexts/MasterDataContext';
import axios from 'axios';
import { MessageSquare, Sparkles, CheckCircle, AlertCircle, Edit2, Calendar, Clock, User, MapPin, CreditCard, Building } from 'lucide-react';

import { BACKEND_URL, API } from '../utils/apiConfig';

const PAYMENT_MODES = ['Cash', 'UPI', 'Online', 'Wallet'];
const BOOKING_SOURCES = ['Khelomore', 'District', 'Hudle', 'Walk-in', 'Offline', 'Others'];

export default function WhatsAppBookingPage() {
  const { getVenueNames, getGamesForVenue, getCourtsForVenueGame, allGames } = useMasterData();
  const VENUES = getVenueNames();
  const GAMES = allGames.length > 0 ? allGames : ['Pickleball', 'Badminton', 'Cricket', 'Football Half', 'Football Full'];
  
  const [message, setMessage] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  const [bookingData, setBookingData] = useState({
    player_name: '',
    player_mobile: '',
    venue: '',
    game: '',
    court: '',
    date: '',
    start_time: '',
    end_time: '',
    amount: '',
    payment_mode: 'Online',
    booking_source: '',
    remarks: ''
  });

  const [detectedSource, setDetectedSource] = useState(null);
  const [confidence, setConfidence] = useState(null);

  const handleParse = async () => {
    if (!message.trim()) {
      toast.error('Please paste a booking message');
      return;
    }

    setParsing(true);
    setParseError(null);
    setParsed(false);

    try {
      const response = await axios.post(`${API}/whatsapp/parse`, { message });
      const data = response.data;
      
      setBookingData({
        player_name: data.player_name || '',
        player_mobile: data.player_mobile || '',
        venue: data.venue || '',
        game: data.game || '',
        court: data.court || '',
        date: data.date || '',
        start_time: data.start_time || '',
        end_time: data.end_time || '',
        amount: data.amount?.toString() || '',
        payment_mode: data.payment_mode || 'Online',
        booking_source: data.booking_source || '',
        remarks: data.remarks || ''
      });
      
      setDetectedSource(data.booking_source);
      setConfidence(data.confidence || 'medium');
      setParsed(true);
      setEditMode(false);
      
      if (data.missing_fields && data.missing_fields.length > 0) {
        toast.warning(`Some fields need attention: ${data.missing_fields.join(', ')}`);
        setEditMode(true);
      } else {
        toast.success('Message parsed successfully! Please review and confirm.');
      }
    } catch (error) {
      console.error('Parse error:', error);
      setParseError(error.response?.data?.detail || 'Failed to parse message');
      toast.error('Failed to parse message. Please check the format.');
    } finally {
      setParsing(false);
    }
  };

  const handleConfirmBooking = async () => {
    // Validate required fields
    const required = ['player_name', 'venue', 'game', 'court', 'date', 'start_time', 'end_time'];
    const missing = required.filter(field => !bookingData[field]);
    
    if (missing.length > 0) {
      toast.error(`Please fill: ${missing.join(', ')}`);
      setEditMode(true);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...bookingData,
        amount: parseFloat(bookingData.amount) || 0,
        remarks: bookingData.remarks || `WhatsApp booking from ${detectedSource || 'unknown source'}`
      };
      
      const response = await axios.post(`${API}/bookings`, payload);
      toast.success('Booking created successfully!');
      
      // Reset form
      setMessage('');
      setParsed(false);
      setBookingData({
        player_name: '',
        player_mobile: '',
        venue: '',
        game: '',
        court: '',
        date: '',
        start_time: '',
        end_time: '',
        amount: '',
        payment_mode: 'Online',
        booking_source: '',
        remarks: ''
      });
      setDetectedSource(null);
      setConfidence(null);
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSaving(false);
    }
  };

  const getCourts = () => {
    if (!bookingData.venue || !bookingData.game) return [];
    return getCourtsForVenueGame(bookingData.venue, bookingData.game);
  };

  const getConfidenceBadge = () => {
    if (!confidence) return null;
    const colors = {
      high: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-red-100 text-red-800'
    };
    return (
      <Badge className={colors[confidence] || colors.medium}>
        {confidence.charAt(0).toUpperCase() + confidence.slice(1)} Confidence
      </Badge>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center justify-center gap-3">
            <MessageSquare className="w-8 h-8 text-green-600" />
            WhatsApp Bookings Parser
          </h1>
          <p className="text-slate-600 mt-1">Paste booking messages from Khelomore, District, Hudle to auto-create bookings</p>
        </div>

        {/* Message Input */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-lg font-semibold">Paste WhatsApp Message</Label>
              <div className="flex gap-2">
                <Badge variant="outline" className="text-xs">Khelomore</Badge>
                <Badge variant="outline" className="text-xs">District</Badge>
                <Badge variant="outline" className="text-xs">Hudle</Badge>
              </div>
            </div>
            
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={`Paste your booking message here...

Example (Khelomore):
Booking received for MAKS PICKLEBALL ACADEMY - Powe . Name- Abhishek, Game Day- 13 Mar '26, at 08:30 PM - 10:00 PM , Property- Court 4 . Amount paid: 890.0

Example (District):
New Play Booking Received. Venue: MAKS x Mihir Sen Sports Complex Pickleball - Court 2 Customer: yash shah Date & Time: 14 Mar 2026 9 PM - 11 PM`}
              className="min-h-[150px] font-mono text-sm"
              data-testid="whatsapp-message-input"
            />
            
            <Button 
              onClick={handleParse} 
              disabled={parsing || !message.trim()}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="parse-button"
            >
              {parsing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Parsing with AI...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Extract Booking Details
                </>
              )}
            </Button>
          </div>
        </Card>

        {/* Parse Error */}
        {parseError && (
          <Card className="p-4 bg-red-50 border-red-200">
            <div className="flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <div>
                <p className="font-medium">Failed to parse message</p>
                <p className="text-sm">{parseError}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Parsed Result */}
        {parsed && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-semibold text-slate-900">Extracted Booking Details</h2>
              </div>
              <div className="flex items-center gap-2">
                {detectedSource && (
                  <Badge className="bg-blue-100 text-blue-800">{detectedSource}</Badge>
                )}
                {getConfidenceBadge()}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setEditMode(!editMode)}
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  {editMode ? 'Preview' : 'Edit'}
                </Button>
              </div>
            </div>

            {editMode ? (
              /* Edit Mode */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Player Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={bookingData.player_name}
                      onChange={(e) => setBookingData({...bookingData, player_name: e.target.value})}
                      className="pl-10"
                      placeholder="Player name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Mobile Number</Label>
                  <Input
                    value={bookingData.player_mobile}
                    onChange={(e) => setBookingData({...bookingData, player_mobile: e.target.value})}
                    placeholder="Mobile number (optional)"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Venue *</Label>
                  <Select 
                    value={bookingData.venue} 
                    onValueChange={(v) => setBookingData({...bookingData, venue: v, court: ''})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select venue" />
                    </SelectTrigger>
                    <SelectContent>
                      {VENUES.map(v => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Game *</Label>
                  <Select 
                    value={bookingData.game} 
                    onValueChange={(v) => setBookingData({...bookingData, game: v, court: ''})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select game" />
                    </SelectTrigger>
                    <SelectContent>
                      {GAMES.map(g => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Court *</Label>
                  <Select 
                    value={bookingData.court} 
                    onValueChange={(v) => setBookingData({...bookingData, court: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select court" />
                    </SelectTrigger>
                    <SelectContent>
                      {getCourts().map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date *</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="date"
                      value={bookingData.date}
                      onChange={(e) => setBookingData({...bookingData, date: e.target.value})}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Start Time *</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={bookingData.start_time}
                      onChange={(e) => setBookingData({...bookingData, start_time: e.target.value})}
                      className="pl-10"
                      placeholder="e.g., 08:30 PM"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>End Time *</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={bookingData.end_time}
                      onChange={(e) => setBookingData({...bookingData, end_time: e.target.value})}
                      className="pl-10"
                      placeholder="e.g., 10:00 PM"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Amount Paid</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="number"
                      value={bookingData.amount}
                      onChange={(e) => setBookingData({...bookingData, amount: e.target.value})}
                      className="pl-10"
                      placeholder="Amount in ₹"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Payment Mode</Label>
                  <Select 
                    value={bookingData.payment_mode} 
                    onValueChange={(v) => setBookingData({...bookingData, payment_mode: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_MODES.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Booking Source</Label>
                  <Select 
                    value={bookingData.booking_source} 
                    onValueChange={(v) => setBookingData({...bookingData, booking_source: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {BOOKING_SOURCES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Remarks</Label>
                  <Input
                    value={bookingData.remarks}
                    onChange={(e) => setBookingData({...bookingData, remarks: e.target.value})}
                    placeholder="Additional notes"
                  />
                </div>
              </div>
            ) : (
              /* Preview Mode */
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <User className="w-4 h-4" />
                    Player Name
                  </div>
                  <p className="font-semibold text-slate-900">{bookingData.player_name || '-'}</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Building className="w-4 h-4" />
                    Venue
                  </div>
                  <p className="font-semibold text-slate-900">{bookingData.venue || '-'}</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <MapPin className="w-4 h-4" />
                    Game & Court
                  </div>
                  <p className="font-semibold text-slate-900">
                    {bookingData.game || '-'} - {bookingData.court || '-'}
                  </p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Calendar className="w-4 h-4" />
                    Date
                  </div>
                  <p className="font-semibold text-slate-900">{bookingData.date || '-'}</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Clock className="w-4 h-4" />
                    Time Slot
                  </div>
                  <p className="font-semibold text-slate-900">
                    {bookingData.start_time || '-'} - {bookingData.end_time || '-'}
                  </p>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
                    <CreditCard className="w-4 h-4" />
                    Amount Paid
                  </div>
                  <p className="font-semibold text-green-700">
                    ₹{bookingData.amount || '0'}
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setParsed(false);
                  setMessage('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmBooking}
                disabled={saving}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="confirm-booking-button"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating Booking...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirm & Create Booking
                  </>
                )}
              </Button>
            </div>
          </Card>
        )}

        {/* Help Section */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-2">Supported Message Formats</h3>
          <div className="space-y-2 text-sm text-blue-700">
            <p><strong>Khelomore:</strong> "Booking received for MAKS PICKLEBALL... Name- [name], Game Day- [date], at [time], Property- [court], Amount paid: [amount]"</p>
            <p><strong>District:</strong> "New Play Booking Received. Venue: [venue] - [court] Customer: [name] Date & Time: [date] [time]"</p>
            <p><strong>Hudle:</strong> Similar format with venue, customer, date/time details</p>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
